// Voicebot.jsx (corrected)
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
// import { useSelector } from "react-redux";
import { ObjectId } from "bson";

// --- LOGGER UTILITY ---
const Logger = {
  log: (category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ${message}`;
    if (data) {
      console.log(logMessage, data);
    } else {
      console.log(logMessage);
    }
  },
  error: (category, message, error = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ❌ ERROR: ${message}`;
    if (error) {
      console.error(logMessage, error);
    } else {
      console.error(logMessage);
    }
  },
  warn: (category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${category}] ⚠️ WARNING: ${message}`;
    if (data) {
      console.warn(logMessage, data);
    } else {
      console.warn(logMessage);
    }
  },
};

// --- VOICE ASSISTANT CONFIGURATION ---
const BASE_URL =
  process.env.REACT_APP_VOICE_TO_VOICE_URL || "http://localhost:8000";
const WS_URL = `${BASE_URL.replace(/^https/, "wss")}/voicetovoice`;

const REQUESTED_UPLINK_SAMPLE_RATE = 16000;
const DEFAULT_TTS_SAMPLE_RATE = 24000;
const PLAYBACK_BUFFER_TARGET_SECONDS = 0.05;

// --- IMPROVED: VOICE ACTIVITY DETECTION (VAD) CONFIGURATION ---
const IMPROVED_VAD_CONFIG = {
  VOICE_ACTIVITY_THRESHOLD: 0.03,
  SILENCE_DURATION_MS: 600,
  ENERGY_CONSISTENCY_WINDOW: 5,
  MIN_ENERGY_RATIO: 0.65,
  SPECTRAL_ROLLOFF_THRESHOLD: 0.88,
  ZCR_THRESHOLD: 0.3,
  SPIKE_THRESHOLD: 1.8,
  CONFIDENCE_THRESHOLD: 0.55,
};

const AITutorPage = () => {
  Logger.log("INIT", "AITutorPage component initializing");

  // --- UI State ---
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // --- Redux State ---
  //   const user = useSelector((state) => state.profile?.user);
  //   Logger.log("INIT", "User from Redux", user);

  // console.log("user",user?._id)

  // --- Context Selection State ---
  const [language, setLanguage] = useState("en-US");
  const [inputLanguage, setInputLanguage] = useState("en-US");
  //   const [userId, setUserId] = useState("");
  const userId = "68d40ab1fb53c6116977ad0d";
  //   useEffect(() => {
  //     if (user?._id) {
  //       setUserId(user.user_id);
  //     }
  //   }, [user]);

  //   useEffect(() => {
  // console.log("userid...",userId, user?.user_id)
  // }, [user,userId]);

  // --- Voice Assistant State ---
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAIResponding, setIsAIResponding] = useState(false);

  const socketRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const inputAudioWorkletNodeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentAudioSourceRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const nextStartTimeRef = useRef(0);
  const isAIRespondingRef = useRef(false);

  // --- VAD Refs ---
  const voiceActivityRef = useRef(false);
  const silenceTimeoutRef = useRef(null);
  const energyHistoryRef = useRef([]);
  const backgroundNoiseRef = useRef(0.001);
  const speechConfidenceRef = useRef(0);
  const endOfTurnTimerRef = useRef(null);

  const serverFormatRef = useRef({
    codec: "pcm_s16le",
    sampleRate: DEFAULT_TTS_SAMPLE_RATE,
    channels: 1,
  });

  const masterGainRef = useRef(null);
  const compressorRef = useRef(null);
  const filterRef = useRef(null);
  const bufferedDurationRef = useRef(0);

  const processingMessages = [
    "Initializing neural core...",
    "Syncing voice patterns...",
    "Optimizing conversational intelligence...",
    "Connecting to your personal assistant...",
    "Ready to listen!",
  ];

  useEffect(() => {
    Logger.log("LOADING", "Starting loading animation");
    const interval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => {
        if (prevIndex === processingMessages.length - 1) {
          Logger.log("LOADING", "Loading complete, stopping animation");
          clearInterval(interval);
          setTimeout(() => {
            Logger.log("LOADING", "Transitioning to interactive view");
            setIsLoading(false);
          }, 500);
          return prevIndex;
        }
        Logger.log("LOADING", "Loading message index", prevIndex + 1);
        return prevIndex + 1;
      });
    }, 700);
    return () => {
      Logger.log("LOADING", "Cleaning up loading animation");
      clearInterval(interval);
    };
  }, [processingMessages.length]);

  // --- CORE AUDIO LOGIC ---

  const ensurePlaybackAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      try {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: serverFormatRef.current.sampleRate,
          latencyHint: "interactive", // Already optimal
        });

        masterGainRef.current = audioCtxRef.current.createGain();
        compressorRef.current = audioCtxRef.current.createDynamicsCompressor();
        filterRef.current = audioCtxRef.current.createBiquadFilter();

        masterGainRef.current.gain.value = 0.9;
        filterRef.current.type = "highpass";
        filterRef.current.frequency.value = 120;
        compressorRef.current.threshold.value = -24;
        compressorRef.current.knee.value = 30;
        compressorRef.current.ratio.value = 3;
        compressorRef.current.attack.value = 0.003;
        compressorRef.current.release.value = 0.25;

        masterGainRef.current
          .connect(filterRef.current)
          .connect(compressorRef.current)
          .connect(audioCtxRef.current.destination);

        // LATENCY OPTIMIZATION: Minimal initial delay
        nextStartTimeRef.current = audioCtxRef.current.currentTime + 0.02; // Reduced from 0.05
      } catch (e) {
        Logger.error("AUDIO_CONTEXT", "Failed to create AudioContext", e);
        setError("Web Audio API is not supported by this browser.");
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stopAndClearPlayback = useCallback((reason = "unknown") => {
    Logger.log("PLAYBACK", `Stopping and clearing playback. Reason: ${reason}`);
    try {
      if (currentAudioSourceRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          Logger.log("PLAYBACK", "Stopping current audio source", {
            currentTime: now,
          });
          currentAudioSourceRef.current.stop(now + 0.001);
          currentAudioSourceRef.current.disconnect();
        } catch (e) {
          Logger.warn("PLAYBACK", "Source already stopped", e.message);
        }
        currentAudioSourceRef.current = null;
      }

      if (masterGainRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          Logger.log("PLAYBACK", "Fading out master gain");
          masterGainRef.current.gain.cancelScheduledValues(now);
          masterGainRef.current.gain.setValueAtTime(
            masterGainRef.current.gain.value,
            now
          );
          masterGainRef.current.gain.exponentialRampToValueAtTime(
            0.001,
            now + 0.01
          );
        } catch (e) {
          Logger.warn("PLAYBACK", "Gain fade error", e.message);
        }
      }

      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        Logger.log("PLAYBACK", "Closing AudioContext");
        audioCtxRef.current.close().catch(() => {});
      }
    } catch (e) {
      Logger.error("PLAYBACK", "Audio cleanup error", e);
    }

    Logger.log("PLAYBACK", "Resetting all playback state");
    audioCtxRef.current = null;
    masterGainRef.current = null;
    compressorRef.current = null;
    filterRef.current = null;
    playbackQueueRef.current = [];
    bufferedDurationRef.current = 0;
    isPlayingRef.current = false;
    isAIRespondingRef.current = false;
    nextStartTimeRef.current = 0;
    setIsAIResponding(false);

    // Clear end-of-turn timer
    if (endOfTurnTimerRef.current) {
      clearTimeout(endOfTurnTimerRef.current);
      endOfTurnTimerRef.current = null;
    }

    Logger.log("PLAYBACK", "Playback cleanup complete");
  }, []);

  // --- VAD HELPER FUNCTIONS ---
  const calculateZCR = useCallback((audioData) => {
    let crossings = 0;
    for (let i = 1; i < audioData.length; i++) {
      if (audioData[i] >= 0 !== audioData[i - 1] >= 0) {
        crossings++;
      }
    }
    const zcr = crossings / audioData.length;
    return zcr;
  }, []);

  const calculateSpectralRolloff = useCallback((audioData) => {
    const highFreqEnergy = audioData
      .slice(audioData.length / 2)
      .reduce((sum, val) => sum + Math.abs(val), 0);
    const totalEnergy = audioData.reduce((sum, val) => sum + Math.abs(val), 0);
    const rolloff = totalEnergy > 0 ? highFreqEnergy / totalEnergy : 0;
    return rolloff;
  }, []);

  // --- IMPROVED: VAD Function with Barge-In Protection ---
  const improvedDetectVoiceActivity = useCallback(
    (audioData) => {
      const {
        VOICE_ACTIVITY_THRESHOLD,
        SILENCE_DURATION_MS,
        ENERGY_CONSISTENCY_WINDOW,
        MIN_ENERGY_RATIO,
        SPECTRAL_ROLLOFF_THRESHOLD,
        ZCR_THRESHOLD,
        SPIKE_THRESHOLD,
        CONFIDENCE_THRESHOLD,
      } = IMPROVED_VAD_CONFIG;

      let sum = 0;
      let peak = 0;
      for (let i = 0; i < audioData.length; i++) {
        const val = audioData[i] * audioData[i];
        sum += val;
        if (val > peak) peak = val;
      }
      const rms = Math.sqrt(sum / audioData.length);

      // Spike Detection - prevents false positives from clicks/claps
      const peakRatio = rms > 0 ? Math.sqrt(peak) / rms : 0;
      if (peakRatio > SPIKE_THRESHOLD) {
        Logger.warn("VAD", "Spike detected, ignoring transient sound", {
          peakRatio,
        });
        return;
      }

      // Dynamic background noise adaptation
      if (!voiceActivityRef.current) {
        backgroundNoiseRef.current =
          backgroundNoiseRef.current * 0.98 + rms * 0.02;
      }

      const zcr = calculateZCR(audioData);
      const spectralRolloff = calculateSpectralRolloff(audioData);
      const dynamicThreshold = Math.max(
        VOICE_ACTIVITY_THRESHOLD,
        backgroundNoiseRef.current * 4
      );

      // Criteria calculation
      const energyCriteria = rms > dynamicThreshold;
      const zcrCriteria = zcr < ZCR_THRESHOLD;
      const spectralCriteria = spectralRolloff < SPECTRAL_ROLLOFF_THRESHOLD;

      energyHistoryRef.current.push(energyCriteria ? 1 : 0);
      if (energyHistoryRef.current.length > ENERGY_CONSISTENCY_WINDOW) {
        energyHistoryRef.current.shift();
      }
      const consistencyRatio =
        energyHistoryRef.current.reduce((s, v) => s + v, 0) /
        energyHistoryRef.current.length;
      const consistencyCriteria = consistencyRatio >= MIN_ENERGY_RATIO;

      // Confidence Score
      let confidence = 0;
      if (energyCriteria) confidence += 0.4;
      if (zcrCriteria) confidence += 0.25;
      if (spectralCriteria) confidence += 0.2;
      if (consistencyCriteria) confidence += 0.15;
      speechConfidenceRef.current = confidence;

      const hasVoice = confidence >= CONFIDENCE_THRESHOLD;

      if (hasVoice) {
        // --- CRITICAL: BARGE-IN PROTECTION ---
        // Only interrupt if AI is responding AND this is NEW voice activity
        if (isAIRespondingRef.current && !voiceActivityRef.current) {
          Logger.log(
            "BARGE_IN",
            "User interrupted AI response - stopping playback"
          );
          stopAndClearPlayback("user_barge_in");
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            Logger.log("WEBSOCKET", "Sending interrupt signal to server");
            socketRef.current.send(JSON.stringify({ type: "interrupt" }));
          }
        }

        if (!voiceActivityRef.current) {
          Logger.log("VAD", "Voice activity started");
          voiceActivityRef.current = true;
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        }
      } else if (voiceActivityRef.current) {
        if (!silenceTimeoutRef.current) {
          Logger.log("VAD", "Starting silence timeout");
          silenceTimeoutRef.current = setTimeout(() => {
            Logger.log("VAD", "Silence detected, voice activity ended");
            voiceActivityRef.current = false;
            energyHistoryRef.current = [];
            silenceTimeoutRef.current = null;
          }, SILENCE_DURATION_MS);
        }
      }
    },
    [calculateZCR, calculateSpectralRolloff, stopAndClearPlayback]
  );

  const int16ToFloat32 = useCallback((int16) => {
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i];
      float32[i] = s < 0 ? s / 32768.0 : s / 32767.0;
    }
    return float32;
  }, []);

  const processAndPlayQueue = useCallback(() => {
    Logger.log("PLAYBACK_QUEUE", "Processing and playing queue", {
      queueLength: playbackQueueRef.current.length,
      isPlaying: isPlayingRef.current,
    });

    const audioCtx = ensurePlaybackAudioContext();
    if (!audioCtx || playbackQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    const maxSamplesToProcess = audioCtx.sampleRate * 0.5; // Reduced from 1.5s
    const chunksToProcess = [];
    let totalSamples = 0;

    for (const chunk of playbackQueueRef.current) {
      if (totalSamples + chunk.samples.length > maxSamplesToProcess) break;
      chunksToProcess.push(chunk);
      totalSamples += chunk.samples.length;
    }

    if (chunksToProcess.length === 0) {
      return;
    }

    playbackQueueRef.current = playbackQueueRef.current.slice(
      chunksToProcess.length
    );

    const consolidatedSamples = new Float32Array(totalSamples);
    let offset = 0;
    for (const chunk of chunksToProcess) {
      consolidatedSamples.set(chunk.samples, offset);
      offset += chunk.samples.length;
    }

    const sampleRate = chunksToProcess[0].sampleRate;
    bufferedDurationRef.current -= (totalSamples / sampleRate) * 1000;

    try {
      const buffer = audioCtx.createBuffer(
        1,
        consolidatedSamples.length,
        sampleRate
      );
      buffer.copyToChannel(consolidatedSamples, 0, 0);

      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(masterGainRef.current);
      currentAudioSourceRef.current = source;

      const now = audioCtx.currentTime;
      const startTime = Math.max(now, nextStartTimeRef.current);
      source.start(startTime);
      nextStartTimeRef.current = startTime + buffer.duration;

      if (!isAIRespondingRef.current) {
        isAIRespondingRef.current = true;
        setIsAIResponding(true);
      }

      source.onended = () => {
        if (currentAudioSourceRef.current === source) {
          currentAudioSourceRef.current = null;
        }
        isPlayingRef.current = false;

        if (playbackQueueRef.current.length > 0) {
          // LATENCY OPTIMIZATION: Process next chunk immediately
          setTimeout(() => processAndPlayQueue(), 0);
        } else {
          if (endOfTurnTimerRef.current) {
            clearTimeout(endOfTurnTimerRef.current);
          }
          endOfTurnTimerRef.current = setTimeout(() => {
            isAIRespondingRef.current = false;
            setIsAIResponding(false);
            endOfTurnTimerRef.current = null;
          }, 200); // Reduced from 300ms
        }
      };
    } catch (err) {
      Logger.error("PLAYBACK_QUEUE", "Error scheduling audio buffer", err);
      isPlayingRef.current = false;
      isAIRespondingRef.current = false;
      setIsAIResponding(false);
    }
  }, [ensurePlaybackAudioContext]);

  // 3. OPTIMIZE handleIncomingAudio - more aggressive playback start:
  const handleIncomingAudio = useCallback(
    (arrayBuffer) => {
      if (endOfTurnTimerRef.current) {
        clearTimeout(endOfTurnTimerRef.current);
        endOfTurnTimerRef.current = null;
      }

      if (arrayBuffer.byteLength % 2 !== 0) {
        arrayBuffer = arrayBuffer.slice(0, arrayBuffer.byteLength - 1);
      }

      const int16 = new Int16Array(arrayBuffer);
      const float32 = int16ToFloat32(int16);
      const sampleRate = serverFormatRef.current.sampleRate;

      playbackQueueRef.current.push({ samples: float32, sampleRate });
      bufferedDurationRef.current += (float32.length / sampleRate) * 1000;

      // LATENCY OPTIMIZATION: Start playback with minimal buffer (50ms)
      const targetBufferMs = PLAYBACK_BUFFER_TARGET_SECONDS * 1000;
      if (
        bufferedDurationRef.current >= targetBufferMs &&
        !isPlayingRef.current
      ) {
        isPlayingRef.current = true;
        processAndPlayQueue();
      }
    },
    [int16ToFloat32, processAndPlayQueue]
  );

  /**
   * connectWS
   * - convId: conversation id for this session
   * - uid: user id string (raw) or null/undefined
   * - lang: language code
   */
  const connectWS = useCallback(
    (convId, uid, outputLang, inputLang) => {
      Logger.log("WEBSOCKET", "Connecting to WebSocket", {
        conversationId: convId,
        url: WS_URL,
        uid,
        outputLang,
        inputLang,
      });
      return new Promise((resolve, reject) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          Logger.log("WEBSOCKET", "Already connected");
          resolve();
          return;
        }
        setConnectionStatus("connecting");
        const ws = new WebSocket(WS_URL);
        socketRef.current = ws;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          Logger.log("WEBSOCKET", "Connection opened");
          setConnectionStatus("connected");

          // SEND RAW USER ID (no ObjectId("...") wrapper)
          const startMessage = {
            type: "start",
            config: {
              language_code: outputLang,
              input_language_code: inputLang,
              user_id: uid || null,
              conversation_id: convId,
            },
          };
          Logger.log("WEBSOCKET", "Sending start message", startMessage);
          ws.send(JSON.stringify(startMessage));
          resolve();
        };

        ws.onmessage = (event) => {
          if (typeof event.data === "string") {
            try {
              const parsed = JSON.parse(event.data);
              Logger.log("WEBSOCKET", "Received message", parsed);

              // --- CRITICAL: CLEAR COMMAND HANDLING ---
              if (parsed?.type === "clear") {
                Logger.log("WEBSOCKET", "Received clear command from server");
                stopAndClearPlayback("server_clear_command");
                if (ws.readyState === WebSocket.OPEN) {
                  Logger.log("WEBSOCKET", "Sending clear acknowledgment");
                  ws.send(JSON.stringify({ type: "clear_ack" }));
                }
                return;
              }

              if (parsed?.type === "format") {
                const newSampleRate =
                  parsed.sampleRate || DEFAULT_TTS_SAMPLE_RATE;
                Logger.log("WEBSOCKET", "Received format update", {
                  newSampleRate,
                });
                serverFormatRef.current = {
                  ...serverFormatRef.current,
                  ...parsed,
                  sampleRate: newSampleRate,
                };
              } else if (parsed?.type === "response_end") {
                Logger.log("WEBSOCKET", "Received response_end");
                // Don't immediately set to false - let the end-of-turn timer handle it
              }
            } catch (e) {
              Logger.error("WEBSOCKET", "Failed to parse server JSON", e);
            }
          } else if (event.data instanceof ArrayBuffer) {
            if (audioCtxRef.current || ensurePlaybackAudioContext()) {
              handleIncomingAudio(event.data);
            }
          }
        };

        ws.onerror = (err) => {
          Logger.error("WEBSOCKET", "WebSocket error", err);
          setConnectionStatus("error");
          setError("WebSocket connection failed.");
          reject(err);
        };

        ws.onclose = () => {
          Logger.log("WEBSOCKET", "Connection closed");
          setConnectionStatus("disconnected");
          setIsRecording(false);
          stopAndClearPlayback("websocket_close");
        };
      });
    },
    [
      language,
      handleIncomingAudio,
      stopAndClearPlayback,
      ensurePlaybackAudioContext,
    ]
  );

  /**
   * startRecording
   * - _userId, _lang: frozen values passed from the UI
   */
  const startRecording = useCallback(
    async (_userId, _outputLang, _inputLang) => {
      Logger.log("RECORDING", "Starting recording", {
        providedUserId: _userId,
        providedLang: _outputLang,
        _inputLang,
      });
      if (isRecording) {
        Logger.warn("RECORDING", "Already recording");
        return;
      }
      setError(null);
      stopAndClearPlayback("user_start_recording");

      try {
        // If caller provided a persistent user id, use it; otherwise create a per-session conversation id.
        const newConversationId = new ObjectId().toHexString();
        const conversationIdToUse = newConversationId;
        Logger.log("RECORDING", "New conversation ID", conversationIdToUse);

        // Connect websocket with frozen uid/lang
        await connectWS(
          conversationIdToUse,
          _userId || null,
          _outputLang || "en-US",
          _inputLang || "en-US"
        );

        Logger.log("RECORDING", "Creating input AudioContext", {
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
        });
        const audioCtx =
          inputAudioContextRef.current ||
          new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
          });
        inputAudioContextRef.current = audioCtx;

        // --- CRITICAL: HIGH-PASS FILTER IN WORKLET ---
        const inputWorkletCode = `
        class AudioProcessor extends AudioWorkletProcessor {
          constructor(options) {
            super();
            this.bufferSize = 1024;
            this._buffer = new Float32Array(0);

            // High-pass filter coefficients for ~400Hz cutoff at 16kHz sample rate
            // This removes low-frequency background noise (HVAC, rumble, etc.)
            this.b0 = 0.9511;
            this.b1 = -1.9022;
            this.b2 = 0.9511;
            this.a1 = -1.9008;
            this.a2 = 0.9048;
            this.x1 = 0; this.x2 = 0; // input history
            this.y1 = 0; this.y2 = 0; // output history
          }

          applyFilter(inputSample) {
            const y = this.b0 * inputSample + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
            this.x2 = this.x1;
            this.x1 = inputSample;
            this.y2 = this.y1;
            this.y1 = y;
            return y;
          }

          process(inputs) {
            const channel = inputs[0]?.[0];
            if (!channel) return true;

            // Apply high-pass filter to remove low-frequency noise
            const filteredChannel = new Float32Array(channel.length);
            for (let i = 0; i < channel.length; i++) {
              filteredChannel[i] = this.applyFilter(channel[i]);
            }

            // Send filtered audio for VAD analysis
            this.port.postMessage({ type: 'voice_activity_data', audioData: Array.from(filteredChannel) });

            // Buffer and send to server
            const newBuf = new Float32Array(this._buffer.length + filteredChannel.length);
            newBuf.set(this._buffer, 0);
            newBuf.set(filteredChannel, this._buffer.length);
            this._buffer = newBuf;

            while (this._buffer.length >= this.bufferSize) {
              const chunk = this._buffer.subarray(0, this.bufferSize);
              const int16 = new Int16Array(chunk.length);
              for (let i = 0; i < chunk.length; i++) {
                let s = Math.max(-1, Math.min(1, chunk[i]));
                int16[i] = s < 0 ? s * 32768 : s * 32767;
              }
              this.port.postMessage({ type: 'audio_data', buffer: int16.buffer }, [int16.buffer]);
              this._buffer = this._buffer.subarray(this.bufferSize);
            }
            return true;
          }
        }
        registerProcessor('audio-processor', AudioProcessor);
      `;

        Logger.log("RECORDING", "Adding audio worklet module");
        if (audioCtx.audioWorklet.addModule) {
          await audioCtx.audioWorklet.addModule(
            URL.createObjectURL(
              new Blob([inputWorkletCode], { type: "application/javascript" })
            )
          );
        }

        // --- CRITICAL: DISABLE AUTO GAIN CONTROL ---
        Logger.log(
          "RECORDING",
          "Requesting microphone access with AGC disabled"
        );
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
            autoGainControl: false, // CRITICAL: Prevents unpredictable volume changes
          },
        });
        Logger.log("RECORDING", "Microphone access granted");
        streamRef.current = stream;

        Logger.log(
          "RECORDING",
          "Creating media stream source and worklet node"
        );
        const source = audioCtx.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioCtx, "audio-processor");

        workletNode.port.onmessage = (event) => {
          if (event.data.type === "voice_activity_data") {
            improvedDetectVoiceActivity(event.data.audioData);
          } else if (event.data.type === "audio_data") {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(event.data.buffer);
            }
          }
        };

        Logger.log("RECORDING", "Connecting audio nodes");
        source.connect(workletNode);
        inputAudioWorkletNodeRef.current = workletNode;
        setIsRecording(true);
        Logger.log("RECORDING", "Recording started successfully");

        if (audioCtx.state === "suspended") {
          Logger.log("RECORDING", "Resuming suspended audio context");
          await audioCtx.resume();
        }
      } catch (err) {
        Logger.error("RECORDING", "Failed to start recording", err);
        setError(
          err.message || "Could not start recording. Check mic permissions."
        );
        setIsRecording(false);
      }
    },
    [isRecording, connectWS, improvedDetectVoiceActivity, stopAndClearPlayback]
  );

  const stopRecording = useCallback(() => {
    Logger.log("RECORDING", "Stopping recording");
    if (!isRecording) {
      Logger.warn("RECORDING", "Not currently recording");
      return;
    }
    setIsRecording(false);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      Logger.log("WEBSOCKET", "Sending stop message");
      socketRef.current.send(JSON.stringify({ type: "stop" }));
    }

    Logger.log("RECORDING", "Stopping media stream tracks");
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    Logger.log("RECORDING", "Disconnecting worklet node");
    inputAudioWorkletNodeRef.current?.disconnect();
    inputAudioWorkletNodeRef.current = null;

    if (
      inputAudioContextRef.current &&
      inputAudioContextRef.current.state !== "closed"
    ) {
      Logger.log("RECORDING", "Closing input audio context");
      inputAudioContextRef.current.close().then(() => {
        Logger.log("RECORDING", "Input audio context closed");
        inputAudioContextRef.current = null;
      });
    }

    if (silenceTimeoutRef.current) {
      Logger.log("RECORDING", "Clearing silence timeout");
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    // Reset VAD state
    voiceActivityRef.current = false;
    energyHistoryRef.current = [];
    backgroundNoiseRef.current = 0.001;
    speechConfidenceRef.current = 0;

    Logger.log("RECORDING", "Recording stopped successfully");
  }, [isRecording]);

  useEffect(() => {
    Logger.log("CLEANUP", "Setting up cleanup effect");
    return () => {
      Logger.log("CLEANUP", "Running cleanup");
      if (isRecording) stopRecording();
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        Logger.log("CLEANUP", "Closing audio context");
        audioCtxRef.current.close();
      }
      if (endOfTurnTimerRef.current) {
        clearTimeout(endOfTurnTimerRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [isRecording, stopRecording]);

  // --- UI Handlers ---
  const handleMicClick = () => {
    Logger.log("UI", "Mic button clicked", { isRecording });
    if (isRecording) {
      stopRecording();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSubmit = async (event) => {
    Logger.log("UI", "Form submitted");
    event.preventDefault();
    const uid = userId.trim() || null;
    const outputLang = language || "en-US";
    const inputLang = inputLanguage || "en-US";

    Logger.log("UI", "Starting session with frozen values", {
      uid,
      outputLang,
      inputLang,
    });
    setIsModalOpen(false);
    startRecording(uid, outputLang, inputLang);
  };

  const viewVariants = {
    hidden: { opacity: 0, scale: 0.9, filter: "blur(20px)" },
    visible: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
      opacity: 0,
      scale: 1.1,
      filter: "blur(20px)",
      transition: { duration: 0.4 },
    },
  };

  const renderLoadingView = () => {
    Logger.log("UI", "Rendering loading view");
    return (
      <motion.div
        key="loading-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center"
      >
        <div className="relative w-40 h-40 mb-10">
          <motion.div
            className="absolute inset-0 border-b-2 border-l-2 border-blue-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
          <div className="absolute inset-4 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full flex items-center justify-center backdrop-blur-3xl">
            <motion.span
              className="text-5xl"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ✨
            </motion.span>
          </div>
        </div>

        <div className="h-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={loadingMessageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xl md:text-2xl text-neutral-300 font-light tracking-wide"
            >
              {processingMessages[loadingMessageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  const renderInteractiveView = () => {
    Logger.log("UI", "Rendering interactive view");

    return (
      <motion.div
        key="interactive-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative flex flex-col items-center justify-center h-full w-full p-4"
      >
        <div className="flex flex-col items-center justify-center flex-grow pt-10">
          <motion.div className="relative flex items-center justify-center">
            {/* AI Pulse Effect */}
            {(isAIResponding ||
              (!isRecording && connectionStatus === "disconnected")) && (
              <motion.div
                className="absolute bg-blue-500/20 rounded-full filter blur-3xl"
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{ width: "160px", height: "160px" }}
              />
            )}

            <motion.button
              className={`relative w-28 h-28 rounded-full flex items-center justify-center text-5xl cursor-pointer transition-all duration-500 border border-white/10 ${
                isRecording
                  ? "bg-red-600 shadow-[0_0_40px_rgba(220,38,38,0.5)]"
                  : "bg-white/5 backdrop-blur-2xl hover:bg-white/10"
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              disabled={connectionStatus === "connecting"}
              onClick={handleMicClick}
              animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
              transition={
                isRecording
                  ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                  : {}
              }
            >
              {isRecording ? (
                <div className="flex items-center justify-center space-x-1.5">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 bg-white rounded-full shadow-lg"
                      animate={{
                        height: [16, 48, 16],
                        opacity: [0.6, 1, 0.6],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.08,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <span className="opacity-80">💠</span>
              )}
            </motion.button>
          </motion.div>

          {/* Status Text */}
          <div className="mt-10 text-xl h-12 flex flex-col items-center justify-center">
            {error ? (
              <span className="text-red-400 font-medium">{error}</span>
            ) : connectionStatus === "connecting" ? (
              <span className="text-neutral-400 animate-pulse tracking-widest text-sm uppercase">
                Initializing...
              </span>
            ) : isRecording ? (
              <span className="text-neutral-200 font-light">
                Stop Recording
              </span>
            ) : isAIResponding ? (
              <span className="text-blue-400 font-light">
                Assistant is responding...
              </span>
            ) : (
              <span className="text-neutral-500 font-light">
                Tap the orb to speak
              </span>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 backdrop-blur-3xl flex items-center justify-center z-50 bg-black/20"
              onClick={() => !isRecording && setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", damping: 20 }}
                className="bg-[#0A0A0A] w-full max-w-sm p-8 rounded-[2.5rem] shadow-2xl border border-white/5"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSubmit}>
                  <h2 className="text-2xl font-bold text-white mb-8 text-center tracking-tight">
                    AI Preferences
                  </h2>
                  <div className="space-y-6">
                    {/* INPUT LANGUAGE */}
                    <div>
                      <label
                        htmlFor="input-language-select"
                        className="block text-[10px] font-bold text-neutral-500 uppercase tracking-widest mb-2 ml-1"
                      >
                        I will speak in
                      </label>

                      <select
                        id="input-language-select"
                        value={inputLanguage}
                        onChange={(e) => {
                          Logger.log(
                            "UI",
                            "Input language changed",
                            e.target.value
                          );
                          setInputLanguage(e.target.value);
                        }}
                        disabled={isRecording}
                        className="w-full px-4 py-3 text-base text-white bg-black border border-neutral-800 rounded-xl focus:ring-1 focus:ring-blue-500 outline-none appearance-none transition-all"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="hi-IN">Hindi (India)</option>
                        <option value="ar-SA">Arabic</option>
                        <option value="fr-FR">French</option>
                        <option value="mr-IN">Marathi</option>
                        <option value="zh-CN">Chinese</option>
                      </select>
                    </div>

                    {/* OUTPUT LANGUAGE */}
                    <div>
                      <label
                        htmlFor="language-select"
                        className="block text-[10px] font-bold text-neutral-500 uppercase tracking-widest mb-2 ml-1"
                      >
                        AI will respond in
                      </label>

                      <select
                        id="language-select"
                        value={language}
                        onChange={(e) => {
                          Logger.log(
                            "UI",
                            "Output language changed",
                            e.target.value
                          );
                          setLanguage(e.target.value);
                        }}
                        disabled={isRecording}
                        className="w-full px-4 py-3 text-base text-white bg-black border border-neutral-800 rounded-xl focus:ring-1 focus:ring-blue-500 outline-none appearance-none transition-all"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="hi-IN">Hindi</option>
                        <option value="ar-SA">Arabic</option>
                        <option value="fr-FR">French</option>
                        <option value="mr-IN">Marathi</option>
                        <option value="zh-CN">Chinese</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="mt-10 w-full px-4 py-4 font-bold bg-white text-black rounded-2xl hover:bg-neutral-200 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                    disabled={isRecording}
                  >
                    Start Interaction
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  Logger.log("UI", "Main render", { isLoading, isRecording, connectionStatus });

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center bg-[#050505] px-4 text-center text-white overflow-hidden font-sans">
      {/* FIX: Pure Background Layer 
          Removed any nested containers that could cause rectangular shadows
      */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(15,23,42,1)_0%,rgba(0,0,0,1)_100%)]" />
        {/* Subtle animated ambient light */}
        <motion.div
          animate={{ opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute inset-0 bg-blue-500/5 mix-blend-screen"
        />
      </div>

      {/* FIX: Transparent Content Wrapper
          Ensuring bg-transparent and no shadow on the parent container
      */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-2xl bg-transparent shadow-none">
        <AnimatePresence mode="wait">
          {isLoading ? renderLoadingView() : renderInteractiveView()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AITutorPage;
